<script setup lang="ts">
import type { Component } from 'vue';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useMediaQuery } from '@vueuse/core';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-vue-next';
import { ContextMenuItem, ContextMenuLabel, ContextMenuSeparator } from '@/components/ui/context-menu';
import { DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';

type StackedActionMenuItem = {
    id: string;
    label: string;
    description?: string;
    icon?: Component;
    disabled?: boolean;
    variant?: 'default' | 'destructive';
    pageId?: string;
    value?: string;
    actionKey?: string;
};

type StackedActionMenuPage = {
    id: string;
    title: string;
    items: StackedActionMenuItem[];
    emptyLabel?: string;
};

const props = withDefaults(defineProps<{
    mode: 'dropdown' | 'context';
    pages: StackedActionMenuPage[];
    open?: boolean;
    initialPageId?: string;
    loading?: boolean;
    loadingLabel?: string;
    emptyLabel?: string;
    maxBodyHeight?: string;
}>(), {
    open: false,
    initialPageId: '',
    loading: false,
    loadingLabel: 'Loading…',
    emptyLabel: 'Nothing to show here yet.',
    maxBodyHeight: 'min(18rem, calc(100vh - 11rem), calc(100svh - 11rem))',
});

const emit = defineEmits<{
    select: [item: StackedActionMenuItem];
}>();

const menuComponents = computed(() => (
    props.mode === 'context'
        ? {
            item: ContextMenuItem,
            label: ContextMenuLabel,
            separator: ContextMenuSeparator,
        }
        : {
            item: DropdownMenuItem,
            label: DropdownMenuLabel,
            separator: DropdownMenuSeparator,
        }
));

const defaultPageId = computed(() => props.initialPageId || props.pages[0]?.id || '');
const currentPageId = ref(defaultPageId.value);
const pageHistory = ref<string[]>([]);
const expandedMobilePageId = ref('');
const scrollBodyRef = ref<HTMLElement | null>(null);
const showBottomFade = ref(false);
const isMobile = useMediaQuery('(max-width: 767px)');

const pagesById = computed(() => new Map(props.pages.map((page) => [page.id, page])));
const currentPage = computed(() => pagesById.value.get(currentPageId.value) || props.pages[0] || null);
const canGoBack = computed(() => pageHistory.value.length > 0);
let resizeObserver: ResizeObserver | null = null;

watch(defaultPageId, (value) => {
    if (!value) return;
    if (!pagesById.value.has(currentPageId.value)) {
        currentPageId.value = value;
        pageHistory.value = [];
    }
});

watch(
    () => props.open,
    async (open) => {
        if (!open) return;
        currentPageId.value = defaultPageId.value;
        pageHistory.value = [];
        expandedMobilePageId.value = '';
        await nextTick();
        updateScrollState();
    },
);

watch(isMobile, () => {
    currentPageId.value = defaultPageId.value;
    pageHistory.value = [];
    expandedMobilePageId.value = '';
});

watch(
    [currentPage, () => props.loading, () => currentPage.value?.items.length ?? 0],
    async () => {
        await nextTick();
        updateScrollState();
    },
    { flush: 'post' },
);

function goToPage(pageId: string) {
    if (!pageId || pageId === currentPageId.value || !pagesById.value.has(pageId)) return;
    pageHistory.value = [...pageHistory.value, currentPageId.value];
    currentPageId.value = pageId;
}

function goBack(event: Event) {
    event.preventDefault();
    const previousPageId = pageHistory.value.at(-1);
    if (!previousPageId) return;
    pageHistory.value = pageHistory.value.slice(0, -1);
    currentPageId.value = previousPageId;
}

function handleItemSelect(event: Event, item: StackedActionMenuItem) {
    if (item.pageId) {
        event.preventDefault();

        if (isMobile.value) {
            expandedMobilePageId.value = expandedMobilePageId.value === item.pageId ? '' : item.pageId;
            nextTick(updateScrollState);
            return;
        }

        goToPage(item.pageId);
        return;
    }

    emit('select', item);
}

function childPageFor(item: StackedActionMenuItem) {
    if (!item.pageId) return null;
    return pagesById.value.get(item.pageId) || null;
}

function updateScrollState() {
    const scrollBody = scrollBodyRef.value;

    if (!scrollBody) {
        showBottomFade.value = false;
        return;
    }

    const canScroll = scrollBody.scrollHeight > scrollBody.clientHeight + 1;
    const isAtBottom = scrollBody.scrollTop + scrollBody.clientHeight >= scrollBody.scrollHeight - 2;

    showBottomFade.value = canScroll && !isAtBottom;
}

onMounted(async () => {
    await nextTick();
    updateScrollState();

    if (typeof ResizeObserver === 'undefined') return;

    resizeObserver = new ResizeObserver(() => {
        updateScrollState();
    });

    if (scrollBodyRef.value) {
        resizeObserver.observe(scrollBodyRef.value);
    }
});

onBeforeUnmount(() => {
    resizeObserver?.disconnect();
    resizeObserver = null;
});
</script>

<template>
    <div class="min-w-0">
        <component :is="menuComponents.label" class="pb-0.5">
            {{ currentPage?.title || 'Actions' }}
        </component>

        <component
            :is="menuComponents.item"
            v-if="canGoBack && !isMobile"
            class="mb-1 gap-2 text-muted-foreground"
            @select="goBack"
        >
            <ChevronLeft class="size-4" />
            Back
        </component>

        <component :is="menuComponents.separator" />

        <div class="relative isolate">
            <div
                ref="scrollBodyRef"
                class="no-scrollbar overflow-y-auto overflow-x-hidden pt-1"
                :style="{ maxHeight: maxBodyHeight }"
                @scroll="updateScrollState"
            >
                <Transition name="stacked-action-menu-panel" mode="out-in">
                    <div :key="currentPage?.id || 'empty'" class="space-y-1 pb-4">
                        <div
                            v-if="loading"
                            class="flex items-center gap-2 px-2 py-4 text-sm text-muted-foreground"
                        >
                            <Loader2 class="size-4 animate-spin" />
                            {{ loadingLabel }}
                        </div>

                        <div
                            v-else-if="!currentPage || currentPage.items.length === 0"
                            class="px-2 py-3 text-sm text-muted-foreground"
                        >
                            {{ currentPage?.emptyLabel || emptyLabel }}
                        </div>

                        <template v-for="item in currentPage?.items || []" :key="item.id">
                            <component
                                :is="menuComponents.item"
                                :disabled="item.disabled"
                                :variant="item.variant || 'default'"
                                class="min-w-0"
                                @select="(event: Event) => handleItemSelect(event, item)"
                            >
                                <component :is="item.icon" v-if="item.icon" class="size-4" />
                                <div class="min-w-0 flex-1">
                                    <p class="truncate">{{ item.label }}</p>
                                    <p v-if="item.description" class="mt-0.5 truncate text-xs text-muted-foreground">
                                        {{ item.description }}
                                    </p>
                                </div>
                                <ChevronRight
                                    v-if="item.pageId"
                                    class="ml-auto size-4 text-muted-foreground transition-transform duration-200"
                                    :class="isMobile && expandedMobilePageId === item.pageId ? 'rotate-90' : ''"
                                />
                            </component>

                            <Transition
                                enter-active-class="transition duration-200 ease-out"
                                enter-from-class="-translate-y-1 opacity-0"
                                leave-active-class="transition duration-150 ease-in"
                                leave-to-class="-translate-y-1 opacity-0"
                            >
                                <div
                                    v-if="isMobile && item.pageId && expandedMobilePageId === item.pageId"
                                    class="ml-3 space-y-0.5 border-l border-border/70 py-1 pl-2"
                                >
                                    <p
                                        v-if="!childPageFor(item) || childPageFor(item)?.items.length === 0"
                                        class="px-2 py-2 text-xs text-muted-foreground"
                                    >
                                        {{ childPageFor(item)?.emptyLabel || emptyLabel }}
                                    </p>
                                    <component
                                        :is="menuComponents.item"
                                        v-for="childItem in childPageFor(item)?.items || []"
                                        :key="childItem.id"
                                        :disabled="childItem.disabled"
                                        :variant="childItem.variant || 'default'"
                                        class="min-w-0"
                                        @select="(event: Event) => handleItemSelect(event, childItem)"
                                    >
                                        <component :is="childItem.icon" v-if="childItem.icon" class="size-4" />
                                        <div class="min-w-0 flex-1">
                                            <p class="truncate">{{ childItem.label }}</p>
                                            <p v-if="childItem.description" class="mt-0.5 truncate text-xs text-muted-foreground">
                                                {{ childItem.description }}
                                            </p>
                                        </div>
                                    </component>
                                </div>
                            </Transition>
                        </template>
                    </div>
                </Transition>
            </div>

            <div
                v-if="showBottomFade"
                aria-hidden="true"
                class="pointer-events-none absolute inset-x-0 bottom-0 z-20 h-14 rounded-b-md"
                style="background: linear-gradient(to bottom, hsl(var(--popover) / 0), hsl(var(--popover) / 0.36) 24%, hsl(var(--popover) / 0.76) 58%, hsl(var(--popover) / 0.96) 86%, hsl(var(--popover)) 100%); box-shadow: inset 0 1px 0 hsl(var(--border) / 0.28), inset 0 22px 24px -24px hsl(var(--foreground) / 0.3);"
            />
        </div>
    </div>
</template>

<style scoped>
.stacked-action-menu-panel-enter-active,
.stacked-action-menu-panel-leave-active {
    transition:
        opacity 180ms cubic-bezier(0.22, 1, 0.36, 1),
        transform 180ms cubic-bezier(0.22, 1, 0.36, 1);
}

.stacked-action-menu-panel-enter-from {
    opacity: 0;
    transform: translateX(8px);
}

.stacked-action-menu-panel-leave-to {
    opacity: 0;
    transform: translateX(-8px);
}
</style>
